import { useState, useCallback } from 'react';
import { buildStandardRequestObject, buildCustomFieldsRequestObject, buildHawkeyeRequestObject, adjustAppointmentDateForPlan, isHawkeyePlanType } from '../helpers/requestBuilders';
import { planHasCustomFields } from '../helpers/planFieldConfigs';
import useAuth from './useAuth';

/**
 * Custom hook for case creation submission
 * Handles building the request object and submitting to the API
 */
const useCaseCreation = () => {
  const { acquireToken } = useAuth();

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [caseId, setCaseId] = useState('');

  /**
   * Creates a new case
   * @param {object} options - Creation options
   * @param {object} options.formData - Form data from useCaseForm
   * @param {object} options.customFieldValues - Custom field values for plan-specific data
   * @param {object} options.planDetails - Plan details from usePlans.getPlanByName()
   * @param {string} options.providerId - Provider ID from usePlans
   * @param {object} [options.orgContext={}] - Organization context for config lookup
   * @param {string} [options.orgContext.orgGroupId] - Organization group ID
   * @param {string} [options.orgContext.organizationId] - Organization ID
   * @returns {Promise<object>} The created case response
   */
  const createCase = useCallback(
    async ({ formData, customFieldValues, planDetails, providerId, orgContext = {} }) => {
      if (!planDetails) {
        throw new Error('Plan details not found');
      }

      setIsSubmitting(true);
      setError(null);
      setCaseId(''); // Reset previous case ID

      try {
        const accessToken = await acquireToken();

        let requestObject;

        if (isHawkeyePlanType(planDetails.type)) {
          // Hawkeye tenant: source THIRD_PARTY, appointments from caseSpecificData
          requestObject = buildHawkeyeRequestObject({
            formData,
            planId: planDetails.id,
            providerId,
            customFieldValues,
            orgContext,
            planType: planDetails.type,
            aptTypes: planDetails.aptTypes || [],
          });
        } else if (planHasCustomFields(formData.plan, orgContext, planDetails.type)) {
          // Other plans with custom caseSpecificData fields
          requestObject = buildCustomFieldsRequestObject({
            formData,
            planId: planDetails.id,
            providerId,
            customFieldValues,
            orgContext,
            planType: planDetails.type,
          });
        } else {
          // Standard plans without custom fields
          requestObject = buildStandardRequestObject({
            formData,
            planId: planDetails.id,
            planType: planDetails.type,
            aptType: planDetails.aptType,
            cdtCodes: planDetails.cdtCodes,
            providerId,
          });

          // Adjust appointment date for specific plans
          adjustAppointmentDateForPlan(requestObject, formData.plan);
        }

        console.log('Request Object:', JSON.stringify(requestObject));

        // Submit to API
        const apiEndpoint = process.env.REACT_APP_CASE_API_ENDPOINT;
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            accept: '*/*',
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestObject),
        });

        // Get response as text first, then try to parse as JSON
        const responseText = await response.text();
        let responseData;

        try {
          responseData = JSON.parse(responseText);
        } catch {
          // Response is plain text, not JSON
          responseData = { message: responseText };
        }

        if (!response.ok) {
          console.error('Error:', responseData);
          // case-service returns 409 with { existingActiveCaseIds: [...] } when a duplicate
          // active case exists for the patient/phone number. Surface the ids in the error
          // message so the QA user can locate or close the conflicting case.
          if (response.status === 409 && Array.isArray(responseData?.existingActiveCaseIds)) {
            const ids = responseData.existingActiveCaseIds.join(', ');
            throw new Error(
              ids
                ? `An active case already exists for this patient. Existing case id(s): ${ids}`
                : 'An active case already exists for this patient.'
            );
          }
          throw new Error(responseData.message || responseText || 'Failed to create case');
        }

        console.log('Response Data:', responseData);
        setCaseId(responseData.id || 'created');

        return responseData;
      } catch (err) {
        console.error('Error creating case:', err);
        setError(err);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [acquireToken]
  );

  /**
   * Reset the case creation state
   */
  const reset = useCallback(() => {
    setCaseId('');
    setError(null);
  }, []);

  return {
    // State
    isSubmitting,
    error,
    caseId,

    // Actions
    createCase,
    reset,
  };
};

export default useCaseCreation;
